import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Trash2, ExternalLink, FileText, Clock, CheckCircle, AlertCircle, Loader2, Building2, Tag, Globe, Power } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import ServiceLLMToggle from '../components/ServiceLLMToggle';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-gray-500/20 text-gray-300 border-gray-500/40', icon: Clock },
  processing: { label: 'Traitement...', color: 'bg-blue-500/20 text-blue-300 border-blue-500/40', icon: Loader2 },
  draft_created: { label: 'Brouillon créé', color: 'bg-green-500/20 text-green-300 border-green-500/40', icon: CheckCircle },
  error: { label: 'Erreur', color: 'bg-red-500/20 text-red-300 border-red-500/40', icon: AlertCircle },
};

export default function CommuniquesPresse() {
  const navigate = useNavigate();
  const [cps, setCps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [serviceRunning, setServiceRunning] = useState(false);
  const [togglingService, setTogglingService] = useState(false);
  const [checkingNow, setCheckingNow] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [cpsRes, statsRes, statusRes] = await Promise.all([
        fetch(`${API}/api/cp?limit=100`, { headers }),
        fetch(`${API}/api/cp/stats`, { headers }),
        fetch(`${API}/api/cp/service/status`, { headers }),
      ]);

      if (cpsRes.ok) {
        const data = await cpsRes.json();
        setCps(data.communiques || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (statusRes.ok) {
        const data = await statusRes.json();
        setServiceRunning(data.running);
      }
    } catch (error) {
      console.error('Erreur chargement CP:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh toutes les 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRetry = async (cpId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/cp/${cpId}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Traitement relancé');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDelete = async (cpId) => {
    if (!window.confirm('Supprimer ce communiqué ?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/cp/${cpId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Communiqué supprimé');
        fetchData();
      } else {
        toast.error('Erreur suppression');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const toggleService = async () => {
    setTogglingService(true);
    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = serviceRunning ? 'stop' : 'start';
      const res = await fetch(`${API}/api/cp/service/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setServiceRunning(data.running);
        toast.success(data.running ? 'Service démarré' : 'Service arrêté');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setTogglingService(false);
    }
  };

  const checkNow = async () => {
    setCheckingNow(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/cp/check-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Vérification lancée - les nouveaux CP seront traités sous peu');
        // Rafraîchir après quelques secondes
        setTimeout(() => fetchData(), 5000);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la vérification');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setCheckingNow(false);
    }
  };

  const filteredCps = filter === 'all' ? cps : cps.filter(cp => cp.status === filter);

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-8" data-testid="cp-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">Auto-CP</h1>
            <p className="text-slate-400 text-sm">Communiqués de Presse → Articles</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Toggle LLM Mode */}
          <ServiceLLMToggle serviceId="cp" />
          
          {/* Contrôle du service */}
          <div className="flex items-center gap-2 bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2">
            <Power className={`w-4 h-4 ${serviceRunning ? 'text-green-400' : 'text-slate-500'}`} />
            <span className="text-sm text-slate-300">{serviceRunning ? 'Actif' : 'Arrêté'}</span>
            <Switch
              checked={serviceRunning}
              onCheckedChange={toggleService}
              disabled={togglingService}
              data-testid="service-toggle"
            />
          </div>
          <Button 
            onClick={checkNow} 
            variant="outline" 
            size="sm" 
            disabled={checkingNow}
            data-testid="check-now-btn"
            className="border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
          >
            {checkingNow ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Vérification...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Vérifier maintenant</>
            )}
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>
      </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-white">{stats.total}</div>
              <div className="text-slate-400 text-sm">Total CP</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-400">{stats.by_status?.draft_created || 0}</div>
              <div className="text-slate-400 text-sm">Brouillons créés</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-400">{stats.by_status?.processing || 0}</div>
              <div className="text-slate-400 text-sm">En traitement</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-slate-400">{stats.by_status?.pending || 0}</div>
              <div className="text-slate-400 text-sm">En attente</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-400">{stats.by_status?.error || 0}</div>
              <div className="text-slate-400 text-sm">Erreurs</div>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'draft_created', 'processing', 'pending', 'error'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'Tous' : statusConfig[f]?.label || f}
            </Button>
          ))}
        </div>

        {/* Liste des CP */}
        <div className="space-y-3">
          {filteredCps.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun communiqué de presse</p>
              <p className="text-sm mt-2">Les CP reçus sur communique-presse@economiematin.fr apparaîtront ici</p>
            </div>
          ) : (
            filteredCps.map((cp) => {
              const StatusIcon = statusConfig[cp.status]?.icon || FileText;
              return (
                <div
                  key={cp.id}
                  className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:border-sky-500/40 transition-colors"
                  data-testid={`cp-${cp.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={statusConfig[cp.status]?.color || 'bg-gray-500/20'}>
                          <StatusIcon className={`w-3 h-3 mr-1 ${cp.status === 'processing' ? 'animate-spin' : ''}`} />
                          {statusConfig[cp.status]?.label || cp.status}
                        </Badge>
                        {cp.thematique && (
                          <Badge variant="outline" className="text-xs">
                            <Tag className="w-3 h-3 mr-1" />
                            {cp.thematique}
                          </Badge>
                        )}
                        {cp.target_site_name && (
                          <Badge variant="outline" className="text-xs text-sky-300 border-sky-500/40">
                            <Globe className="w-3 h-3 mr-1" />
                            {cp.target_site_name}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-slate-50 truncate">{cp.subject || '(Sans sujet)'}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-300">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {cp.from_name || cp.from_email || 'Inconnu'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(cp.created_at)}
                        </span>
                        {cp.attachments?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {cp.attachments.length} PJ
                          </span>
                        )}
                      </div>
                      {cp.error_message && (
                        <p className="text-red-400 text-sm mt-2 truncate">{cp.error_message}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {cp.wordpress_edit_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(cp.wordpress_edit_url, '_blank')}
                          data-testid={`cp-${cp.id}-wp-btn`}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" /> WordPress
                        </Button>
                      )}
                      {(cp.status === 'error' || cp.status === 'pending') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRetry(cp.id)}
                          data-testid={`cp-${cp.id}-retry-btn`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDelete(cp.id)}
                        data-testid={`cp-${cp.id}-delete-btn`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Info box */}
        <div className="mt-8 bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
          <h3 className="font-medium text-sky-300 mb-2">Comment ça marche ?</h3>
          <ul className="text-sm text-slate-300 space-y-1">
            <li>• Les communiqués de presse envoyés à <strong className="text-sky-300">communique-presse@economiematin.fr</strong> sont automatiquement récupérés</li>
            <li>• L'IA analyse le contenu et détecte la thématique (économie, politique, énergie, santé, tech, transport, défense...)</li>
            <li>• Un article est généré et routé vers le site correspondant en brouillon</li>
            <li>• Vérification toutes les 10 minutes</li>
          </ul>
        </div>
    </div>
  );
}
